// sim/genePower.ts — GENE POWER SCORE: a PURE, DISPLAY-ONLY read-model that distils a
// gene's strength into one comparable integer, plus a reward-vs-incumbent comparison.
//
// WHAT IT IS: a single legible "Power Score" for a gene, so the player can read at a
// glance whether a fresh Evolution-Box reward is an UPGRADE, a SIDEGRADE, or a
// DOWNGRADE against the gene they already hold in that slot. The score is grounded in
// the gene's EXPRESSED EFFECT_CHANNEL vector (its real in-sim contribution), then
// amplified by the five loot-quality dimensions that make a drop feel rare:
//   rarity, mutation count (Σ trait stacks), internal synergy (distinct co-active
//   traits), upgrade level (ascension), and evolution tier (splice generation).
//
// CRITICAL — this is NOT a tick System, and NOT sim state (mirrors src/sim/diagnostics.ts
// and the render-only layers in DESIGN.md §19/§24):
//   • It NEVER mutates the GeneInventory or any sim state.
//   • It NEVER draws RNG — it is a deterministic pure function of integer accessors.
//   • It is NOT folded into any state hash and is NOT part of the save payload. Compute
//     it before or after a tick and the world hash is byte-identical.
//   • It reads only already-deterministic integer state through GeneInventory accessors,
//     so same seed + same tick ⇒ identical Power Score (replay-safe).
//
// ALLOCATION: genePower / genePowerParts allocate nothing. The inventory-bound helpers
// reuse a single module-level scratch buffer for expressGene (no per-call Int32Array),
// so they are cheap to call per-frame for the HUD. Pure integer arithmetic throughout,
// in a fixed term order.

import { EFFECT_CHANNEL_COUNT, GENE_TRAIT_COUNT } from '../types.ts';
import type { GeneInventory, GeneId } from './genes.ts';

// ---------------------------------------------------------------------------
// Weights. Each is the per-unit contribution of one dimension; the score is a
// fixed-order weighted sum, so every dimension is independently monotonic.
// ---------------------------------------------------------------------------

// REBALANCE (rarity refactor): rarity used to be the dominant flat term (a high-rarity
// gene "won" on tier alone). It is now a modest floor; a gene's score is driven by what
// makes it INTERESTING — its expressed effect, the BREADTH of distinct co-active traits
// (synergy), its mutation stacks, ascension investment, and splice/evolution tier. So a
// trait-rich, synergistic Rare can out-score a thin Legendary, and rarity reads as
// character (see src/sim/rarityPerks.ts) rather than "the same gene, bigger number".

/** Floor so any real gene reads as having weight even before amplifiers. */
export const POWER_BASE = 40;
/** Per-point of expressed channel magnitude (L1 norm of the effect vector) — the gene's
 *  real in-sim contribution, now a leading term. */
export const POWER_EXPR_SCALE = 13;
/** Per rarity tier (Common=0 … Legendary=4) — a modest floor, no longer dominant. */
export const POWER_RARITY_STEP = 20;
/** Per mutation stack (Σ trait counts) — unique traits matter more. */
export const POWER_MUTATION_STEP = 22;
/** Per distinct co-active trait (internal synergy) — now a top driver of value. */
export const POWER_SYNERGY_STEP = 48;
/** Per ascension/upgrade level (player investment). */
export const POWER_UPGRADE_STEP = 95;
/** Per splice generation (evolution-tier interactions). */
export const POWER_TIER_STEP = 44;
/** Half-width of the "sidegrade" band: |delta| within this reads as a sidegrade. */
export const POWER_SIDEGRADE_BAND = 25;

// ---------------------------------------------------------------------------
// Pure scoring (no inventory dependency — directly unit-testable).
// ---------------------------------------------------------------------------

/** The five loot-quality dimensions plus the expressed-channel magnitude. */
export interface GenePowerInputs {
  rarity: number;
  mutationCount: number;
  synergyCount: number;
  upgradeLevel: number;
  evolutionTier: number;
  exprPower: number;
}

/** Per-dimension contribution breakdown; `total` is their sum (== genePower). */
export interface GenePowerParts {
  base: number;
  expr: number;
  rarity: number;
  mutation: number;
  synergy: number;
  upgrade: number;
  tier: number;
  total: number;
}

/**
 * Decompose a gene's Power Score into its per-dimension contributions. Each term is
 * `weight * max(0, dimension)` (negatives clamped, so the score can never go below the
 * base floor), and `total` is their fixed-order sum. Every term is independently
 * monotonic non-decreasing in its input. Pure integer; allocates one small object.
 */
export function genePowerParts(i: GenePowerInputs): GenePowerParts {
  const base = POWER_BASE;
  const expr = POWER_EXPR_SCALE * Math.max(0, i.exprPower);
  const rarity = POWER_RARITY_STEP * Math.max(0, i.rarity);
  const mutation = POWER_MUTATION_STEP * Math.max(0, i.mutationCount);
  const synergy = POWER_SYNERGY_STEP * Math.max(0, i.synergyCount);
  const upgrade = POWER_UPGRADE_STEP * Math.max(0, i.upgradeLevel);
  const tier = POWER_TIER_STEP * Math.max(0, i.evolutionTier);
  const total = base + expr + rarity + mutation + synergy + upgrade + tier;
  return { base, expr, rarity, mutation, synergy, upgrade, tier, total };
}

/** A gene's single Power Score = sum of all weighted dimension contributions. */
export function genePower(i: GenePowerInputs): number {
  return genePowerParts(i).total;
}

// ---------------------------------------------------------------------------
// Inventory-bound helpers. Reuse one scratch buffer for expressGene so a per-frame
// HUD pass allocates nothing. `upgradeLevel` (ascension) is supplied by the caller
// because it lives outside the engine inventory (web/geneProgression.ts).
// ---------------------------------------------------------------------------

/** Module-level reused scratch for expressGene — no per-call Int32Array allocation. */
const _scratch = new Int32Array(EFFECT_CHANNEL_COUNT);

/**
 * Read a gene's Power inputs straight off the inventory accessors. `exprPower` is the
 * L1 norm (Σ |channel|) of the gene's expressed (soft-capped) effect vector — its real
 * in-sim magnitude. `synergyCount` is the number of DISTINCT traits with a positive
 * stack (breadth, rewarding multi-trait genes). rarity / mutationCount / evolutionTier
 * come from the accessors; `upgradeLevel` (ascension) is passed through. Allocation-free
 * when the shared scratch is used.
 */
export function genePowerInputsOf(
  inv: GeneInventory,
  id: GeneId,
  upgradeLevel = 0,
  scratch: Int32Array = _scratch,
): GenePowerInputs {
  inv.expressGene(id, scratch);
  let exprPower = 0;
  for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
    const v = scratch[c] as number;
    exprPower += v < 0 ? -v : v;
  }
  let synergyCount = 0;
  for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
    if ((inv.traitOf(id, t) as number) > 0) synergyCount++;
  }
  return {
    rarity: inv.rarityOf(id) as number,
    mutationCount: inv.traitTotalOf(id) as number,
    synergyCount,
    upgradeLevel,
    evolutionTier: inv.generationOf(id) as number,
    exprPower,
  };
}

/** A gene's Power Score, read straight from the inventory (ascension-aware). */
export function genePowerOf(
  inv: GeneInventory,
  id: GeneId,
  upgradeLevel = 0,
  scratch: Int32Array = _scratch,
): number {
  return genePower(genePowerInputsOf(inv, id, upgradeLevel, scratch));
}

/** A gene's Power breakdown, read straight from the inventory (ascension-aware). */
export function genePowerPartsOf(
  inv: GeneInventory,
  id: GeneId,
  upgradeLevel = 0,
  scratch: Int32Array = _scratch,
): GenePowerParts {
  return genePowerParts(genePowerInputsOf(inv, id, upgradeLevel, scratch));
}

// ---------------------------------------------------------------------------
// Reward-vs-incumbent comparison (the ▲/▼/— verdict for the box reveal).
// ---------------------------------------------------------------------------

export type PowerVerdict = 'upgrade' | 'sidegrade' | 'downgrade';

/**
 * Finer, PERCENTAGE-based grade for the loot-comparison UI (the colour code): a brand-new
 * slot, a MEANINGFUL upgrade (green), a MINOR upgrade (yellow), a lateral SIDEGRADE (gray),
 * or a DOWNGRADE (red). Percentage-based so it stays honest as gene power grows — a +30 on a
 * 100-power gene is a meaningful +30%, the same +30 on a 1000-power gene is a minor +3%.
 */
export type UpgradeGrade = 'new' | 'meaningful' | 'minor' | 'sidegrade' | 'downgrade';

/** ≥ this PERCENT improvement reads as a meaningful upgrade (green). */
export const UPGRADE_MEANINGFUL_PCT = 10;
/** ≥ this PERCENT (and below meaningful) reads as a minor upgrade (yellow); within ±this is a sidegrade. */
export const UPGRADE_MINOR_PCT = 3;

export interface PowerComparison {
  /** reward power − incumbent power (0 when there is no incumbent). */
  delta: number;
  /** Percentage improvement over the incumbent (round(100·delta/incumbent); 0 when none). */
  pct: number;
  verdict: PowerVerdict;
  /** Finer, %-based grade for the colour code (new/meaningful/minor/sidegrade/downgrade). */
  grade: UpgradeGrade;
  /** The incumbent's power (0 when there is none). */
  incumbentPower: number;
  /** True when the player holds nothing comparable in this slot yet. */
  isNew: boolean;
}

/**
 * Grade a comparison for the colour code: a new slot is always a win; otherwise the PERCENT
 * improvement buckets it (≥ MEANINGFUL → green, ≥ MINOR → yellow, within ±MINOR → gray,
 * below −MINOR → red). Pure; mirrors `powerVerdict` but split by magnitude for the UI.
 */
export function upgradeGrade(delta: number, incumbentPower: number, isNew: boolean): UpgradeGrade {
  if (isNew) return 'new';
  if (incumbentPower <= 0) return delta > 0 ? 'meaningful' : 'sidegrade';
  const pct = (100 * delta) / incumbentPower;
  if (pct >= UPGRADE_MEANINGFUL_PCT) return 'meaningful';
  if (pct >= UPGRADE_MINOR_PCT) return 'minor';
  if (pct <= -UPGRADE_MINOR_PCT) return 'downgrade';
  return 'sidegrade';
}

/**
 * Bucket a power delta into a verdict: clearly above the sidegrade band → upgrade,
 * clearly below → downgrade, otherwise a sidegrade (roughly a lateral move). Pure.
 */
export function powerVerdict(delta: number, band = POWER_SIDEGRADE_BAND): PowerVerdict {
  if (delta > band) return 'upgrade';
  if (delta < -band) return 'downgrade';
  return 'sidegrade';
}

/**
 * Compare a reward gene's Power against the incumbent the player currently holds in
 * the same slot. A null incumbent means a brand-new slot — always framed as an
 * upgrade (the player can only improve). Otherwise the delta drives the verdict. Pure.
 */
export function comparePower(
  rewardPower: number,
  incumbentPower: number | null,
  band = POWER_SIDEGRADE_BAND,
): PowerComparison {
  if (incumbentPower === null) {
    return { delta: 0, pct: 0, verdict: 'upgrade', grade: 'new', incumbentPower: 0, isNew: true };
  }
  const delta = rewardPower - incumbentPower;
  const pct = incumbentPower > 0 ? Math.round((100 * delta) / incumbentPower) : 0;
  return {
    delta,
    pct,
    verdict: powerVerdict(delta, band),
    grade: upgradeGrade(delta, incumbentPower, false),
    incumbentPower,
    isNew: false,
  };
}
